/**
 * PRD Review Store - Zustand store for PRD Review UI-only state.
 *
 * Pattern: docs/design-patterns/frontend/state-management.md
 * Pattern: docs/design-patterns/frontend/sse-consumption.md#feature-specific-sse-event-processing
 *
 * IMPORTANT: This store only holds CLIENT-SIDE UI state.
 * Server state (session, chat history) is managed by React Query.
 *
 * Manages:
 * - View mode (markdown/preview)
 * - Section highlight state
 * - Pending message content (for optimistic UI)
 *
 * Does NOT manage (these are in React Query):
 * - Session data
 * - Chat history
 * - Agent typing state (derived from is_agent_editing)
 */

import { create } from 'zustand';

import type { ReviewViewMode } from '~/types';

import { logger } from '../utils/logger';

// ============================================================================
// Types
// ============================================================================

interface PRDReviewUIState {
  // UI state
  viewMode: ReviewViewMode;
  lastChangedSection: string | null; // Section name that was last updated (for highlighting)

  // Optimistic UI state
  pendingMessageContent: string | null; // Content being sent (shown as "sending...")

  // Actions
  setViewMode: (mode: ReviewViewMode) => void;
  setLastChangedSection: (sectionName: string | null) => void;
  setPendingMessage: (content: string | null) => void;
  reset: () => void;
}

// ============================================================================
// Store
// ============================================================================

/**
 * Zustand store for PRD Review UI-only state.
 *
 * Server state (session, messages, typing) is managed by React Query
 * in usePRDReview hook. This store only holds ephemeral UI preferences.
 *
 * @example
 * ```tsx
 * const viewMode = usePRDReviewUIStore(state => state.viewMode);
 * const setViewMode = usePRDReviewUIStore(state => state.setViewMode);
 * ```
 */
export const usePRDReviewUIStore = create<PRDReviewUIState>()(set => ({
  // Initial state
  viewMode: 'preview',
  lastChangedSection: null,
  pendingMessageContent: null,

  // Actions
  setViewMode: (mode: ReviewViewMode): void => {
    set({ viewMode: mode });
  },

  setLastChangedSection: (sectionName: string | null): void => {
    set({ lastChangedSection: sectionName });
  },

  setPendingMessage: (content: string | null): void => {
    set({ pendingMessageContent: content });
  },

  reset: (): void => {
    logger.info({ component: 'prdReviewUIStore' }, 'Resetting UI state');
    set({
      viewMode: 'preview',
      lastChangedSection: null,
      pendingMessageContent: null,
    });
  },
}));

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Optimized selector for view mode.
 */
export const useReviewViewMode = (): ReviewViewMode => usePRDReviewUIStore(state => state.viewMode);

/**
 * Optimized selector for pending message content.
 */
export const usePendingMessage = (): string | null =>
  usePRDReviewUIStore(state => state.pendingMessageContent);
